/**
 * API Rate Limiter and Request Tracker
 * Prevents exceeding daily/monthly API limits
 */

class APILimiter {
  constructor() {
    // Load from localStorage
    this.dailyLimit = parseInt(process.env.REACT_APP_DAILY_API_LIMIT) || 150; // Default: 150 requests/day
    this.monthlyLimit = parseInt(process.env.REACT_APP_MONTHLY_API_LIMIT) || 5000; // Default: 5000 requests/month

    this.loadStats();
  }

  loadStats() {
    try {
      const saved = localStorage.getItem('api_usage_stats');
      if (saved) {
        const stats = JSON.parse(saved);
        this.dailyCount = stats.dailyCount || 0;
        this.monthlyCount = stats.monthlyCount || 0;
        this.lastResetDate = stats.lastResetDate || new Date().toDateString();
        this.lastResetMonth = stats.lastResetMonth || new Date().toISOString().slice(0, 7); // YYYY-MM

        // Reset daily count if it's a new day
        if (this.lastResetDate !== new Date().toDateString()) {
          this.dailyCount = 0;
          this.lastResetDate = new Date().toDateString();
        }

        // Reset monthly count if it's a new month
        const currentMonth = new Date().toISOString().slice(0, 7);
        if (this.lastResetMonth !== currentMonth) {
          this.monthlyCount = 0;
          this.lastResetMonth = currentMonth;
        }
      } else {
        this.dailyCount = 0;
        this.monthlyCount = 0;
        this.lastResetDate = new Date().toDateString();
        this.lastResetMonth = new Date().toISOString().slice(0, 7);
      }
    } catch (error) {
      console.error('Error loading API stats:', error);
      this.dailyCount = 0;
      this.monthlyCount = 0;
      this.lastResetDate = new Date().toDateString();
      this.lastResetMonth = new Date().toISOString().slice(0, 7);
    }
  }

  saveStats() {
    try {
      const stats = {
        dailyCount: this.dailyCount,
        monthlyCount: this.monthlyCount,
        lastResetDate: this.lastResetDate,
        lastResetMonth: this.lastResetMonth
      };
      localStorage.setItem('api_usage_stats', JSON.stringify(stats));
    } catch (error) {
      console.error('Error saving API stats:', error);
    }
  }

  canMakeRequest() {
    this.loadStats(); // Refresh stats

    const dailyRemaining = this.dailyLimit - this.dailyCount;
    const monthlyRemaining = this.monthlyLimit - this.monthlyCount;

    return {
      allowed: dailyRemaining > 0 && monthlyRemaining > 0,
      dailyRemaining,
      monthlyRemaining,
      dailyCount: this.dailyCount,
      monthlyCount: this.monthlyCount,
      dailyLimit: this.dailyLimit,
      monthlyLimit: this.monthlyLimit
    };
  }

  recordRequest() {
    this.loadStats();
    this.dailyCount++;
    this.monthlyCount++;
    this.saveStats();

    // Log warning if approaching limits
    const dailyPercent = (this.dailyCount / this.dailyLimit) * 100;
    const monthlyPercent = (this.monthlyCount / this.monthlyLimit) * 100;

    if (dailyPercent >= 80 || monthlyPercent >= 80) {
      console.warn(`⚠️ API Usage Warning: Daily ${dailyPercent.toFixed(1)}% | Monthly ${monthlyPercent.toFixed(1)}%`);
    }

    if (dailyPercent >= 95 || monthlyPercent >= 95) {
      console.error(`🚨 API Usage Critical: Daily ${dailyPercent.toFixed(1)}% | Monthly ${monthlyPercent.toFixed(1)}%`);
    }
  }

  getStats() {
    this.loadStats();
    return {
      daily: {
        used: this.dailyCount,
        limit: this.dailyLimit,
        remaining: this.dailyLimit - this.dailyCount,
        percent: ((this.dailyCount / this.dailyLimit) * 100).toFixed(1)
      },
      monthly: {
        used: this.monthlyCount,
        limit: this.monthlyLimit,
        remaining: this.monthlyLimit - this.monthlyCount,
        percent: ((this.monthlyCount / this.monthlyLimit) * 100).toFixed(1)
      }
    };
  }

  resetStats() {
    this.dailyCount = 0;
    this.monthlyCount = 0;
    this.lastResetDate = new Date().toDateString();
    this.lastResetMonth = new Date().toISOString().slice(0, 7);
    this.saveStats();
  }
}

// Singleton instance
const apiLimiter = new APILimiter();

export default apiLimiter;
